// src/pages/superadmin/SuperAdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LayoutDashboard, Building2, Users, Building, Key, Package,
  Lock, Cpu, Camera, TrendingUp, TrendingDown, RefreshCw,
  Clock, CheckCircle, XCircle, Shield, Globe, Mail, Phone
} from 'lucide-react';

// Import des sous-composants
import SuperAdminAirports from './SuperAdminAirports';
import SuperAdminStaff from './SuperAdminStaff';
import SuperAdminCompany from './SuperAdminCompany';
import SuperAdminPasswords from './SuperAdminPasswords';
import PackagesManager from './PackagesManager';
import LicensesManager from './LicensesManager';
import AgentsManager from './AgentsManager';
import DetectionsViewer from './DetectionsViewer';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── SparkLine (inspiré de TakeoffLandingAnalytics) ──────────────────────────
const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 160 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#sg-${color.replace('#', '')})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// ─── StatCard (inspiré de TakeoffLandingAnalytics) ───────────────────────────
const StatCard = ({ title, value, sub, icon: Icon, color, trend, sparkData }) => (
  <div className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300">
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" style={{ color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      {sparkData && (
        <div className="mt-2 opacity-70">
          <SparkLine data={sparkData} color={color} height={36} width={140} />
        </div>
      )}
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-emerald-400" />}
          {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
            {sub}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── Onglets disponibles ─────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-400' },
  { id: 'airports', label: 'Airports', icon: Building2, color: 'text-red-400' },
  { id: 'staff', label: 'Staff', icon: Users, color: 'text-green-400' },
  { id: 'company', label: 'Company', icon: Building, color: 'text-purple-400' },
  { id: 'passwords', label: 'Passwords', icon: Key, color: 'text-yellow-400' },
  { id: 'packages', label: 'Packages', icon: Package, color: 'text-indigo-400' },
  { id: 'licenses', label: 'Licenses', icon: Lock, color: 'text-orange-400' },
  { id: 'agents', label: 'Agents', icon: Cpu, color: 'text-cyan-400' },
  { id: 'detections', label: 'Detections', icon: Camera, color: 'text-pink-400' },
];

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [globalStats, setGlobalStats] = useState({
    totalAirports: 0,
    activeAirports: 0,
    totalStaff: 0,
    totalClients: 0,
    totalAgents: 0,
    activeAgents: 0,
    monthlyRevenue: 0,
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/platform/stats`);
      setGlobalStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      setTimeout(() => setAnimated(true), 100);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-4">
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                title="Total Airports"
                value={globalStats.totalAirports}
                sub={`${globalStats.activeAirports} actifs`}
                icon={Building2}
                color="#f87171"
                trend="up"
              />
              <StatCard
                title="Staff"
                value={globalStats.totalStaff}
                sub="Tous aéroports"
                icon={Users}
                color="#4ade80"
              />
              <StatCard
                title="Agents"
                value={`${globalStats.activeAgents}/${globalStats.totalAgents}`}
                sub="Actifs / Total"
                icon={Cpu}
                color="#38bdf8"
              />
              <StatCard
                title="Monthly Revenue"
                value={`$${(globalStats.monthlyRevenue / 1000).toFixed(1)}k`}
                sub="Tous aéroports"
                icon={Globe}
                color="#fbbf24"
                trend="up"
              />
            </div>

            {/* Airports quick list */}
            <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Building2 size={15} className="text-red-400" />
                Airports Overview
              </h3>
              <div className="space-y-2">
                <p className="text-gray-500 text-sm text-center py-4">
                  Cliquez sur l'onglet Airports pour voir la liste complète
                </p>
              </div>
            </div>
          </div>
        );
      case 'airports':
        return <SuperAdminAirports />;
      case 'staff':
        return <SuperAdminStaff />;
      case 'company':
        return <SuperAdminCompany />;
      case 'passwords':
        return <SuperAdminPasswords />;
      case 'packages':
        return <PackagesManager />;
      case 'licenses':
        return <LicensesManager />;
      case 'agents':
        return <AgentsManager />;
      case 'detections':
        return <DetectionsViewer />;
      default:
        return null;
    }
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 color-red-400" />
            Platform Admin
          </h1>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs horizontales */}
      <div className="border-b border-gray-700 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? `${tab.color} border-current`
                  : 'text-gray-400 hover:text-white border-transparent'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <div className="animate-fade-in">
        {renderContent()}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SuperAdminDashboard;